import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Select } from '@/components/Select'
import { SortableTable, type Column } from '@/components/SortableTable'
import { ChangeLogDetailDialog } from './ChangeLogDetailDialog'
import { actionLabel, entityLabel, type AuditActor, type AuditPage, type AuditRow } from './types'
import { fmtDateTime, fmtNumber, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { changelogMessages } from '@/i18n/messages/changelog'

type ChangelogKey = keyof typeof changelogMessages.en

const PAGE_SIZE = 50

const ACTION_STYLES: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  update: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  delete: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
}

// Labels are message keys, translated when rendered.
const ENTITY_OPTIONS: { value: string; label: ChangelogKey }[] = [
  { value: '', label: 'allAreas' },
  { value: 'session', label: 'entitySession' },
  { value: 'project', label: 'entityProject' },
  { value: 'rtk_segment', label: 'entityRtkSegment' },
  { value: 'project_request', label: 'entityProjectRequest' },
  { value: 'bonus_config', label: 'entityBonusConfig' },
]

const ACTION_OPTIONS: { value: string; label: ChangelogKey }[] = [
  { value: '', label: 'allActions' },
  { value: 'create', label: 'actionCreate' },
  { value: 'update', label: 'actionUpdate' },
  { value: 'delete', label: 'actionDelete' },
]

function ActionBadge({ action }: { action: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_STYLES[action] ?? 'bg-neutral-100 text-neutral-600'}`}>
      {actionLabel(action)}
    </span>
  )
}

function changeSummary(row: AuditRow, removedLabel: (value: string) => string): string {
  const short = (v: string | null) => (v == null ? '∅' : v.length > 40 ? v.slice(0, 40) + '…' : v)
  if (row.action === 'delete') return removedLabel(short(row.oldValue))
  if (row.oldValue != null || row.newValue != null) return `${short(row.oldValue)} → ${short(row.newValue)}`
  return '—'
}

const selectClass =
  'rounded-lg border-neutral-300 bg-white px-2.5 py-1.5 text-sm ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-700'

export function ChangeLogPage() {
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [actorId, setActorId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<AuditRow | null>(null)
  const t = useT(changelogMessages)
  const tc = useT(commonMessages)

  const { data: actors } = useQuery<AuditActor[]>({
    queryKey: ['audit-actors'],
    queryFn: () => api.get<AuditActor[]>('/api/audit/actors'),
  })

  const qs = new URLSearchParams()
  if (entityType) qs.set('entityType', entityType)
  if (action) qs.set('action', action)
  if (actorId) qs.set('actorId', actorId)
  if (from) qs.set('from', from)
  if (to) qs.set('to', to)
  if (search.trim()) qs.set('search', search.trim())
  qs.set('page', String(page))
  qs.set('pageSize', String(PAGE_SIZE))

  const { data, isPending } = useQuery<AuditPage>({
    queryKey: ['audit', entityType, action, actorId, from, to, search.trim(), page],
    queryFn: () => api.get<AuditPage>(`/api/audit?${qs.toString()}`),
  })

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const columns: Column<AuditRow>[] = [
    {
      key: 'when',
      header: t('colWhen'),
      sortValue: (r) => r.occurredAt,
      render: (r) => (
        <span className="whitespace-nowrap text-neutral-600 dark:text-neutral-300">
          {fmtDateTime(r.occurredAt)}
        </span>
      ),
    },
    {
      key: 'who',
      header: t('colWho'),
      sortValue: (r) => (r.actorName ?? r.actorId).toLowerCase(),
      render: (r) => <span className="font-medium">{r.actorName ?? r.actorId}</span>,
    },
    {
      key: 'action',
      header: t('colAction'),
      sortValue: (r) => r.action,
      render: (r) => <ActionBadge action={r.action} />,
    },
    {
      key: 'what',
      header: t('colWhat'),
      sortValue: (r) => r.entityType,
      render: (r) => entityLabel(r.entityType),
    },
    {
      key: 'field',
      header: t('colField'),
      sortValue: (r) => r.field ?? '',
      render: (r) => (r.field ? <code className="text-xs">{r.field}</code> : <span className="text-neutral-400">—</span>),
    },
    {
      key: 'change',
      header: t('colChange'),
      render: (r) => <span className="text-neutral-600 dark:text-neutral-300">{changeSummary(r, (value) => t('removed', { value }))}</span>,
    },
    {
      key: 'detail',
      header: '',
      render: (r) => (
        <button
          onClick={() => setSelected(r)}
          className="rounded-lg px-2 py-1 text-xs text-brand-700 ring-1 ring-brand-200 hover:bg-brand-50 dark:text-brand-200 dark:ring-brand-700/40 dark:hover:bg-brand-700/20"
        >
          {t('details')}
        </button>
      ),
    },
  ]

  const resetPageThen = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold">{tc('navChangelog')}</h1>
        <span className="text-sm text-neutral-500">
          {t('changes', { count: total })}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select className="min-w-[10rem]" value={entityType} onChange={resetPageThen(setEntityType)}
          options={ENTITY_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))} />
        <Select className="min-w-[10rem]" value={action} onChange={resetPageThen(setAction)}
          options={ACTION_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))} />
        <Select className="min-w-[12rem]" value={actorId} onChange={resetPageThen(setActorId)}
          empty={{ value: '', label: t('allUsers') }}
          options={(actors ?? []).map((a) => ({ value: a.id, label: `${a.name ?? a.id} (${fmtNumber(a.edits)})` }))} />
        <input
          type="date"
          className={selectClass}
          value={from}
          onChange={(e) => resetPageThen(setFrom)(e.target.value)}
          aria-label={t('fromDate')}
        />
        <input
          type="date"
          className={selectClass}
          value={to}
          onChange={(e) => resetPageThen(setTo)(e.target.value)}
          aria-label={t('toDate')}
        />
        <input
          type="search"
          placeholder={t('searchPlaceholder')}
          className={`${selectClass} min-w-0 w-full flex-1 sm:min-w-56`}
          value={search}
          onChange={(e) => resetPageThen(setSearch)(e.target.value)}
        />
      </div>

      {isPending ? (
        <p className="py-10 text-center text-sm text-neutral-500">{tc('loading')}</p>
      ) : (
        <SortableTable
          columns={columns}
          rows={rows}
          rowKey={(r) => String(r.id)}
          initialSort={{ key: 'when', dir: 'desc' }}
          empty={t('empty')}
        />
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-lg px-3 py-1.5 ring-1 ring-neutral-300 hover:bg-neutral-100 disabled:opacity-40 dark:ring-neutral-700 dark:hover:bg-neutral-800"
          >
            {t('prev')}
          </button>
          <span className="text-neutral-500">
            {t('pageOf', { page, pages })}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="rounded-lg px-3 py-1.5 ring-1 ring-neutral-300 hover:bg-neutral-100 disabled:opacity-40 dark:ring-neutral-700 dark:hover:bg-neutral-800"
          >
            {t('next')}
          </button>
        </div>
      )}

      {selected && <ChangeLogDetailDialog row={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
